// Company-currency helpers (Phase C multi-region: IN/INR, AE/AED, ZA/ZAR).
// Mirrors the company-timezone.ts caching pattern: a module-level value
// synced by useCompanyCurrency() on profile load, plus a localStorage
// fallback so full page reloads render the right symbol before the
// company query resolves.

export const DEFAULT_COMPANY_CURRENCY = 'INR'

const STORAGE_KEY = 'setu.company_currency'

// Locale per supported currency; anything unknown falls back to en-US.
const CURRENCY_LOCALES: Record<string, string> = {
  INR: 'en-IN',
  AED: 'en-AE',
  ZAR: 'en-ZA',
}

function readStoredCurrency(): string | null {
  try {
    return typeof window !== 'undefined'
      ? window.localStorage.getItem(STORAGE_KEY)
      : null
  } catch {
    return null
  }
}

let activeCurrency = readStoredCurrency() || DEFAULT_COMPANY_CURRENCY

export function getCompanyCurrency(): string {
  return activeCurrency
}

export function setCompanyCurrency(currency: string): void {
  if (currency) {
    activeCurrency = currency
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, currency)
      }
    } catch {
      // localStorage unavailable (private mode / SSR) — module cache still works.
    }
  }
}

/**
 * Format a money value in the given (or company's) currency with the
 * right locale + symbol. Accepts the raw decimal strings the API sends.
 * `options` lets callers tweak fraction digits (e.g. whole-number KPIs).
 */
export function formatMoney(
  amount: number | string,
  currency?: string,
  options?: Intl.NumberFormatOptions,
): string {
  const cur = currency || getCompanyCurrency()
  const n = typeof amount === 'string' ? Number(amount) : amount
  if (n === null || n === undefined || Number.isNaN(n)) {
    // Mirror the old per-component fmt() behaviour: show the raw value
    // rather than "NaN" when the API sends something unparseable.
    return String(amount ?? '')
  }
  const locale = CURRENCY_LOCALES[cur] ?? 'en-US'
  const merged: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: cur,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  }
  // Intl throws if min > max (e.g. caller passes maximumFractionDigits: 0
  // over our 2-digit default) — clamp the minimum to keep it valid.
  if (
    merged.maximumFractionDigits !== undefined &&
    (merged.minimumFractionDigits ?? 0) > merged.maximumFractionDigits
  ) {
    merged.minimumFractionDigits = merged.maximumFractionDigits
  }
  return new Intl.NumberFormat(locale, merged).format(n)
}
